import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useForm } from 'react-hook-form';
import { Button } from '../../../../ui/components/ui/button';
import { Form } from '../../../../ui/components/ui/form';
import { Error as ErrorI } from '../../../root/context';
import { BillingTermMetaResponseDto } from '../../../products/types/response';
import { OFFER_CONFLICTS } from '../../constants/offer-conflicts';
import { OfferFormPanel } from '../../constants/offer-form-panel.enum';
import { AvailabilityCheckFn } from '../../hooks/use-availability-check';
import { useOfferFormPanels } from '../../hooks/use-offer-form-panels';
import { CorrectOfferRequestDto } from '../../types/request';
import { OfferAcceptanceResponseDto, OfferCorrectionPreviewResponseDto, OfferFormOptionsResponseDto, OfferResponseDto } from '../../types/response';
import OfferAcceptanceProof from '../offer-acceptance-proof/offer-acceptance-proof';
import OfferAddOnSection from '../offer-add-on-section/offer-add-on-section';
import OfferCorrectionDialog from '../offer-correction-dialog/offer-correction-dialog';
import OfferCustomerSection from '../offer-customer-section/offer-customer-section';
import OfferPaymentSection from '../offer-payment-section/offer-payment-section';
import OfferProductSection from '../offer-product-section/offer-product-section';
import { OfferFormValues } from '../offer-form/offer-form.schema';
import { offerCorrectionSchema, toCorrectOfferRequest, toOfferCorrectionValues } from './offer-correction-form.schema';

export type OfferCorrectionPreviewResult = { preview: OfferCorrectionPreviewResponseDto } | { error: ErrorI | null };

export interface OfferCorrectionFormProps {
  /** Accepted and not archived. */
  offer: OfferResponseDto;
  acceptance: OfferAcceptanceResponseDto;
  billingTerms: BillingTermMetaResponseDto[];
  /** Active products and add-ons plus the contract's own. */
  options: OfferFormOptionsResponseDto;
  /** The contract's start date plus every upcoming 1st/15th. */
  startDateOptions: string[];
  onCancel: () => void;
  /** Renders the change mail without saving. */
  onPreview: (payload: CorrectOfferRequestDto) => Promise<OfferCorrectionPreviewResult>;
  /** Saves and mails the change. Resolves `null` on success, otherwise the error of the request. */
  onSave: (payload: CorrectOfferRequestDto) => Promise<ErrorI | null>;
  checkCompanyNameAvailability?: AvailabilityCheckFn;
}

/** The contract data of an accepted offer on the left, the proof of the acceptance on the right. Saved only after checking the mail. */
const OfferCorrectionForm = ({
  offer,
  acceptance,
  billingTerms,
  options,
  startDateOptions,
  onCancel,
  onPreview,
  onSave,
  checkCompanyNameAvailability
}: OfferCorrectionFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The checked payload with the mail it produces, waiting for the confirmation.
  const [pending, setPending] = useState<{ payload: CorrectOfferRequestDto; preview: OfferCorrectionPreviewResponseDto } | null>(null);
  const { panelProps, openPanelsFor, handleInvalid } = useOfferFormPanels();

  const form = useForm<OfferFormValues>({
    resolver: zodResolver(offerCorrectionSchema),
    defaultValues: toOfferCorrectionValues(offer)
  });

  const showConflict = (error: ErrorI | null) => {
    const conflict = error?.entity ? OFFER_CONFLICTS[error.entity] : undefined;
    if (!conflict) return;
    // Rendered first – a collapsed panel has no input to focus.
    flushSync(() => openPanelsFor([OfferFormPanel.CUSTOMER]));
    form.setError(conflict.field, { message: conflict.message }, { shouldFocus: true });
  };

  const handlePreview = async (values: OfferFormValues) => {
    const payload = toCorrectOfferRequest(values);
    setIsSubmitting(true);
    try {
      const result = await onPreview(payload);
      if ('preview' in result) setPending({ payload, preview: result.preview });
      else showConflict(result.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async (): Promise<boolean> => {
    if (!pending) return true;
    const error = await onSave(pending.payload);
    if (!error) return true;
    // Closed first – the dialog hands the focus back to its button when it closes, the conflict field takes it after that.
    flushSync(() => setPending(null));
    showConflict(error);
    return true;
  };

  return (
    <Form {...form}>
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <form noValidate onSubmit={form.handleSubmit(handlePreview, handleInvalid)} className="flex min-w-0 flex-col gap-5">
          <OfferProductSection
            form={form}
            billingTerms={billingTerms}
            products={options.products}
            panel={panelProps(OfferFormPanel.PRODUCT)}
            detailsPanel={panelProps(OfferFormPanel.PRODUCT_DETAILS)}
            contractProduct={offer.product}
          />
          <OfferAddOnSection control={form.control} addOns={options.addOns} panel={panelProps(OfferFormPanel.ADD_ONS)} />
          <OfferCustomerSection
            control={form.control}
            panel={panelProps(OfferFormPanel.CUSTOMER)}
            checkCompanyNameAvailability={checkCompanyNameAvailability}
            isCorrection
          />
          <OfferPaymentSection control={form.control} startDateOptions={startDateOptions} panel={panelProps(OfferFormPanel.PAYMENT)} isCorrection />

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Änderungen prüfen
            </Button>
          </div>
        </form>

        <OfferAcceptanceProof control={form.control} mail={offer.customer.mail} acceptance={acceptance} />
      </div>

      <OfferCorrectionDialog
        preview={pending?.preview ?? null}
        mail={offer.customer.mail}
        onClose={() => setPending(null)}
        onConfirm={handleConfirm}
      />
    </Form>
  );
};

export default OfferCorrectionForm;
